/**
 * On-canvas touch controls.
 *
 * Design notes, since the choices are not arbitrary:
 *
 * - The movement stick maps to the SAME four booleans the keyboard sets, so a touch player
 *   gets exactly the 8-way movement a keyboard player gets. Feeding an analog vector into
 *   Player would have made touch strictly better than keyboard and changed the game's feel.
 *
 * - The stick is *dynamic*: it materialises wherever the thumb lands in the left zone rather
 *   than sitting at a fixed spot. On a phone held in landscape you cannot reliably find a
 *   fixed stick without looking, and looking away from the arena is death in this game.
 *
 * - Combat mode's FIRE button auto-aims at the nearest enemy on a plain hold, but drag it and
 *   it becomes an aim stick. Aiming is a real mechanic (the Java build aims at the cursor), so
 *   it is preserved — but requiring manual aim for every shot on a touchscreen would make
 *   Combat unplayable, hence the auto-aim default.
 *
 * Everything is drawn in the game's own logical coordinate space, so it scales and letterboxes
 * with the rest of the frame and needs no DOM overlay.
 */
import { Color } from './java/Color';
import { Graphics2D, Font, BasicStroke } from './java/Graphics2D';
import { Game } from './Game';
import { STATE } from './STATE';
import { ID } from './ID';
import { Player } from './Player';
import { GameObject } from './GameObject';
import { GamePalette } from './GamePalette';
import { HUD } from './HUD';

interface Btn {
  id: string;
  cx: number;
  cy: number;
  r: number;
  label: string;
  /** 0..1 — how "lit" the button is (held, or ability ready). */
  glow: number;
}

const ENEMY_IDS = new Set<ID>([
  ID.BasicEnemy, ID.FastEnemy, ID.SmartEnemy, ID.HardEnemy, ID.EnemyBoss,
]);

export class TouchControls {
  // ---------------------------------------------------------------- diagnostics
  //
  // Enabled with ?touchdebug=1. A touch bug on a real phone is otherwise invisible: there is
  // no console to watch and no way to know whether the browser is even delivering pointermove.
  // This draws the raw event stream on the canvas so it can be read off the device itself.

  static debugEnabled = false;
  /** Set by Game — reported in the panel, because "touch was never detected" is a prime suspect. */
  static debugIsTouch = false;
  private static readonly LOG_MAX = 8;
  private static log: string[] = [];
  private static counts: Record<string, number> = { down: 0, move: 0, up: 0, CANCEL: 0 };
  private static readonly FONT_DBG = new Font('Arial', Font.BOLD, 13);

  static debugLog(kind: string, id: number, x: number, y: number, ptrType: string): void {
    if (!TouchControls.debugEnabled) return;
    TouchControls.counts[kind] = (TouchControls.counts[kind] ?? 0) + 1;
    // pointermove floods; keep only a rolling sample so the panel stays readable.
    if (kind === 'move' && TouchControls.counts.move % 6 !== 0) return;
    TouchControls.log.push(`${kind} id=${id} ${ptrType} (${x},${y})`);
    if (TouchControls.log.length > TouchControls.LOG_MAX) TouchControls.log.shift();
  }

  /** Drawn regardless of isTouch — "touch was never detected" is itself a candidate failure. */
  renderDebugPanel(g: Graphics2D): void {
    const player = this.findPlayer();
    const c = TouchControls.counts;

    const lines = [
      `isTouch=${TouchControls.debugIsTouch}  WIDTH=${Game.WIDTH} scale=${Game.getScale().toFixed(2)}`,
      `events: down=${c.down} move=${c.move} up=${c.up} CANCEL=${c.CANCEL}`,
      `stickPointer=${this.stickPointer}  firePointer=${this.firePointer}`,
      player
        ? `move: U=${player.moveUp?1:0} D=${player.moveDown?1:0} L=${player.moveLeft?1:0} R=${player.moveRight?1:0}  x=${Math.round(player.getX())}`
        : 'no player',
      '--- last events ---',
      ...TouchControls.log,
    ];

    const w = 340;
    const h = 22 + lines.length * 17;
    g.setColor(new Color(0, 0, 0, 205));
    g.fillRoundRect(12, 150, w, h, 8, 8);
    g.setColor(new Color(78, 205, 196, 200));
    g.drawRoundRect(12, 150, w, h, 8, 8);

    g.setFont(TouchControls.FONT_DBG);
    let y = 150 + 20;
    for (const line of lines) {
      // CANCEL is the smoking gun for a joystick that dies mid-drag — make it stand out.
      g.setColor(line.startsWith('CANCEL') || (line.startsWith('events') && c.CANCEL > 0)
        ? new Color(235, 87, 87)
        : new Color(220, 228, 238));
      g.drawString(line, 22, y);
      y += 17;
    }
  }

  private static readonly STICK_R = 76;      // outer ring radius
  private static readonly KNOB_R = 34;
  private static readonly DEADZONE = 16;     // px before a direction registers
  private static readonly AIM_DEADZONE = 28; // drag this far from where the thumb landed to aim manually

  private static readonly FONT_BTN = new Font('Arial', Font.BOLD, 15);
  private static readonly FONT_BTN_SM = new Font('Arial', Font.BOLD, 12);

  /**
   * Movement stick — null when no thumb is down in the left zone.
   *
   * The sentinel is null, NOT -1. A touch identifier is an opaque integer and is allowed to be
   * negative: Chrome on iOS hands out ids like -1665224791. Sentinelling with -1 and testing
   * `>= 0` for "is the stick live" therefore silently never matched, so the stick never ran --
   * while the buttons, which are tracked in a Map keyed by id, kept working. That is exactly
   * the "buttons fine, joystick dead" bug. Never assume the sign of a pointer id.
   */
  private stickPointer: number | null = null;
  private stickOX = 0;
  private stickOY = 0;
  private stickX = 0;
  private stickY = 0;

  /**
   * FIRE stick (combat). Its origin is where the thumb *landed*, not the button's drawn centre:
   * a thumb parks anywhere inside the 54px button and creeps while held, so measuring the drag
   * from the centre would flip a plain hold into manual aim and kill the auto-aim default.
   */
  private firePointer: number | null = null;
  private fireOX = 0;
  private fireOY = 0;
  private fireX = 0;
  private fireY = 0;
  private fireManual = false;

  /** pointerId -> button id, for buttons currently held. */
  private held = new Map<number, string>();
  /** Button id -> decaying press flash. */
  private flash = new Map<string, number>();

  private buttons: Btn[] = [];

  // ---------------------------------------------------------------- layout

  /** Rebuilt each frame — WIDTH changes on resize/rotate. */
  private layout(): Btn[] {
    const W = Game.WIDTH;
    const H = Game.HEIGHT;
    const combat = Game.combatMode;
    const b: Btn[] = [];

    if (combat) {
      b.push({ id: 'fire', cx: W - 100, cy: H - 105, r: 54, label: 'FIRE', glow: 0 });
      b.push({ id: 'dash', cx: W - 216, cy: H - 128, r: 42, label: 'DASH', glow: 0 });
      b.push({ id: 'slow', cx: W - 108, cy: H - 232, r: 38, label: 'SLOW', glow: 0 });
      b.push({ id: 'parry', cx: W - 220, cy: H - 250, r: 38, label: 'PRY', glow: 0 });
    } else {
      b.push({ id: 'dash', cx: W - 104, cy: H - 108, r: 54, label: 'DASH', glow: 0 });
      b.push({ id: 'slow', cx: W - 214, cy: H - 190, r: 42, label: 'SLOW', glow: 0 });
    }

    // Utility, tucked under the HUD's whole right column: the upgrade tiers sit at y 18..62 and
    // the achievement toast at y 80..128 (x >= WIDTH-300). At r=26 with a 10px press glow,
    // cy 166 clears the toast (top edge y 130) instead of being painted over it.
    b.push({ id: 'shop', cx: W - 108, cy: 166, r: 26, label: 'SHOP', glow: 0 });
    b.push({ id: 'pause', cx: W - 44, cy: 166, r: 26, label: 'II', glow: 0 });

    return b;
  }

  /** The left region where a thumb summons the movement stick. Avoids the HUD. */
  private inStickZone(x: number, y: number): boolean {
    return x < Game.WIDTH * 0.46 && y > 150;
  }

  /**
   * Nearest centre wins rather than first match: neighbouring slop circles overlap (shop and
   * pause sit 64px apart with a 38px effective radius each), and a first-match scan would hand
   * every tap in the overlap to whichever button happened to be pushed first.
   */
  private hitButton(x: number, y: number): Btn | null {
    let best: Btn | null = null;
    let bestD = Infinity;
    for (const b of this.buttons) {
      const dx = x - b.cx;
      const dy = y - b.cy;
      const d = dx * dx + dy * dy;
      // Generous hit slop — fingers are not cursors.
      const r = b.r + 12;
      if (d <= r * r && d < bestD) {
        bestD = d;
        best = b;
      }
    }
    return best;
  }

  private findPlayer(): Player | null {
    const objs = Game.getHandler()?.getObjects() ?? [];
    for (const o of objs) if (o instanceof Player) return o;
    return null;
  }

  // ---------------------------------------------------------------- input

  /**
   * @returns true if this pointer was consumed by the touch UI (caller must not forward it
   *          on to Menu/Shop, or a tap on FIRE would also register as a menu click).
   */
  pointerDown(pointerId: number, x: number, y: number): boolean {
    const state = Game.gameState as STATE;
    const inGame = state === STATE.Game;
    const inShop = state === STATE.Shop;
    if (!inGame && !inShop) return false;

    this.buttons = this.layout();

    const btn = this.hitButton(x, y);
    if (btn) {
      // In the Shop overlay only the utility buttons are live; the rest is the shop's own UI.
      if (inShop && btn.id !== 'shop' && btn.id !== 'pause') return false;
      this.held.set(pointerId, btn.id);
      this.flash.set(btn.id, 1);
      this.press(btn.id);
      if (btn.id === 'fire') {
        this.firePointer = pointerId;
        this.fireOX = x;
        this.fireOY = y;
        this.fireX = x;
        this.fireY = y;
        this.fireManual = false;
        const p = this.findPlayer();
        if (Game.combatMode && p) this.updateAim(p); // aim before the Player's next tick shoots
      }
      return true;
    }

    if (inShop) return false; // let the Shop UI have it

    if (this.stickPointer === null && this.inStickZone(x, y)) {
      this.stickPointer = pointerId;
      this.stickOX = x;
      this.stickOY = y;
      this.stickX = x;
      this.stickY = y;
      return true;
    }

    // A stray touch elsewhere in the arena: swallow it so it can't be read as a menu click.
    return true;
  }

  pointerMove(pointerId: number, x: number, y: number): boolean {
    if (pointerId === this.stickPointer) {
      this.stickX = x;
      this.stickY = y;
      return true;
    }
    if (pointerId === this.firePointer) {
      this.fireX = x;
      this.fireY = y;
      const dx = x - this.fireOX;
      const dy = y - this.fireOY;
      this.fireManual = dx * dx + dy * dy > TouchControls.AIM_DEADZONE * TouchControls.AIM_DEADZONE;
      return true;
    }
    return this.held.has(pointerId);
  }

  pointerUp(pointerId: number): boolean {
    let consumed = false;

    if (pointerId === this.stickPointer) {
      this.stickPointer = null;
      const p = this.findPlayer();
      if (p) {
        p.moveUp = false;
        p.moveDown = false;
        p.moveLeft = false;
        p.moveRight = false;
      }
      consumed = true;
    }

    if (this.held.delete(pointerId)) consumed = true;

    if (pointerId === this.firePointer) {
      // Another finger may still be on FIRE (a second tap re-binds firePointer). Hand the
      // stick over to it instead of going dead while the button is still visibly held.
      let next: number | null = null;
      for (const [id, btnId] of this.held) {
        if (btnId === 'fire') {
          next = id;
          break;
        }
      }
      this.firePointer = next;
      this.fireManual = false;
      if (next === null) Game.mouseShootHeld = false;
      consumed = true;
    }

    return consumed;
  }

  /** Release everything — called when the game loses focus or leaves the Game state. */
  releaseAll(): void {
    this.stickPointer = null;
    this.firePointer = null;
    this.fireManual = false;
    this.held.clear();
    Game.mouseShootHeld = false;
    const p = this.findPlayer();
    if (p) {
      p.moveUp = false;
      p.moveDown = false;
      p.moveLeft = false;
      p.moveRight = false;
    }
  }

  private press(id: string): void {
    const p = this.findPlayer();
    switch (id) {
      case 'dash':
        if (p) p.dashInput = true;
        break;
      case 'slow':
        if (p) p.slowmoInput = true;
        break;
      case 'parry':
        if (p) p.parryInput = true;
        break;
      case 'fire':
        Game.mouseShootHeld = true;
        break;
      case 'shop':
        // Same toggle the Space key performs.
        Game.gameState = Game.gameState === STATE.Shop ? STATE.Game : STATE.Shop;
        break;
      case 'pause':
        if (Game.gameState === STATE.Paused) {
          Game.gameState = Game.pausedFrom;
        } else {
          Game.pausedFrom = Game.gameState as STATE;
          Game.gameState = STATE.Paused;
        }
        break;
    }
  }

  // ---------------------------------------------------------------- tick

  private prevState: STATE | null = null;

  tick(): void {
    for (const [k, v] of this.flash) {
      const n = v * 0.86;
      if (n < 0.02) this.flash.delete(k);
      else this.flash.set(k, n);
    }

    // Leaving gameplay (death, quit, pause) with a thumb still down would strand the held
    // inputs — mouseShootHeld in particular survives into the next run and auto-fires.
    const state = Game.gameState as STATE;
    const live = state === STATE.Game || state === STATE.Shop;
    const wasLive = this.prevState === STATE.Game || this.prevState === STATE.Shop;
    if (wasLive && !live) this.releaseAll();
    this.prevState = state;

    if (state !== STATE.Game) return;
    const player = this.findPlayer();
    if (!player) return;

    // --- movement stick -> the same four booleans the keyboard drives ---
    if (this.stickPointer !== null) {
      let dx = this.stickX - this.stickOX;
      let dy = this.stickY - this.stickOY;
      const len = Math.sqrt(dx * dx + dy * dy);
      const dz = TouchControls.DEADZONE;

      if (len <= dz) {
        player.moveUp = player.moveDown = player.moveLeft = player.moveRight = false;
      } else {
        // Normalise to an 8-way decision using a 22.5-degree hysteresis band, so diagonals
        // are as easy to hold as cardinals rather than being a knife-edge between them.
        const ang = Math.atan2(dy, dx);
        const oct = Math.round(ang / (Math.PI / 4)); // -4..4
        const right = oct === 0 || oct === 1 || oct === -1;
        const left = oct === 4 || oct === -4 || oct === 3 || oct === -3;
        const down = oct === 1 || oct === 2 || oct === 3;
        const up = oct === -1 || oct === -2 || oct === -3;
        player.moveRight = right;
        player.moveLeft = left;
        player.moveDown = down;
        player.moveUp = up;
      }
    }

    // --- combat aiming ---
    if (Game.combatMode && this.firePointer !== null) {
      Game.mouseShootHeld = true;
      this.updateAim(player);
    }
  }

  /**
   * Writes the aim point Player.shoot() reads. Also called the instant FIRE goes down, because
   * the Player ticks before we do: the shot it takes on that very frame would otherwise fly at
   * the previous hold's aim — or, on the first press of a run, at the initial 0,0.
   */
  private updateAim(player: Player): void {
    const px = player.getX() + 24;
    const py = player.getY() + 24;

    if (this.fireManual) {
      // Drag direction becomes the aim vector, projected well past the player.
      const ax = this.fireX - this.fireOX;
      const ay = this.fireY - this.fireOY;
      const l = Math.sqrt(ax * ax + ay * ay) || 1;
      Game.mouseGameX = Math.trunc(px + (ax / l) * 400);
      Game.mouseGameY = Math.trunc(py + (ay / l) * 400);
    } else {
      const target = this.nearestEnemy(px, py);
      if (target) {
        Game.mouseGameX = Math.trunc(target.getX() + 16);
        Game.mouseGameY = Math.trunc(target.getY() + 16);
      } else {
        // Nothing to shoot — fire the way we're moving, else straight ahead.
        const vx = player.getVelX();
        const vy = player.getVelY();
        const l = Math.sqrt(vx * vx + vy * vy);
        Game.mouseGameX = Math.trunc(px + (l > 0.5 ? (vx / l) * 400 : 400));
        Game.mouseGameY = Math.trunc(py + (l > 0.5 ? (vy / l) * 400 : 0));
      }
    }
  }

  private nearestEnemy(px: number, py: number): GameObject | null {
    const objs = Game.getHandler()?.getObjects() ?? [];
    let best: GameObject | null = null;
    let bestD = Infinity;
    for (const o of objs) {
      if (!ENEMY_IDS.has(o.getId())) continue;
      const dx = o.getX() + 16 - px;
      const dy = o.getY() + 16 - py;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = o;
      }
    }
    return best;
  }

  // ---------------------------------------------------------------- render

  render(g: Graphics2D): void {
    const state = Game.gameState as STATE;
    if (state !== STATE.Game && state !== STATE.Shop) return;

    this.buttons = this.layout();
    const inShop = state === STATE.Shop;
    const player = this.findPlayer();

    // Movement stick
    if (!inShop && this.stickPointer !== null) {
      this.renderStick(g);
    } else if (!inShop && this.stickPointer === null) {
      this.renderStickHint(g);
    }

    for (const b of this.buttons) {
      if (inShop) {
        if (b.id !== 'shop' && b.id !== 'pause') continue;
        this.renderButton(g, b, true, b.id === 'shop' ? 'CLOSE' : b.label);
        continue;
      }
      this.renderButton(g, b, this.isEnabled(b.id, player), b.label);
    }
  }

  /** Grey a button out when its ability has no charges — same information the HUD shows. */
  private isEnabled(id: string, player: Player | null): boolean {
    if (!player) return false;
    switch (id) {
      case 'dash': return player.getDashCooldownPct() <= 0;
      case 'slow': return player.getSlowmoCharges() > 0;
      default: return true;
    }
  }

  private renderStick(g: Graphics2D): void {
    const accent = GamePalette.accent();

    let dx = this.stickX - this.stickOX;
    let dy = this.stickY - this.stickOY;
    const len = Math.sqrt(dx * dx + dy * dy);
    const max = TouchControls.STICK_R - TouchControls.KNOB_R;
    if (len > max) {
      dx = (dx / len) * max;
      dy = (dy / len) * max;
    }

    const ox = Math.trunc(this.stickOX);
    const oy = Math.trunc(this.stickOY);
    const R = TouchControls.STICK_R;

    // Outer ring
    g.setStroke(new BasicStroke(2));
    g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), 60));
    g.drawOval(ox - R, oy - R, R * 2, R * 2);
    g.setColor(new Color(230, 234, 240, 14));
    g.fillOval(ox - R, oy - R, R * 2, R * 2);

    // Knob
    const kx = Math.trunc(ox + dx);
    const ky = Math.trunc(oy + dy);
    const K = TouchControls.KNOB_R;
    g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), 40));
    g.fillOval(kx - K - 6, ky - K - 6, (K + 6) * 2, (K + 6) * 2);
    g.setColor(new Color(230, 234, 240, 190));
    g.fillOval(kx - K, ky - K, K * 2, K * 2);
    g.setStroke(new BasicStroke(1));
  }

  /** A faint resting hint so a first-time player knows the left side is the stick. */
  private renderStickHint(g: Graphics2D): void {
    const x = Math.trunc(Game.WIDTH * 0.16);
    const y = Game.HEIGHT - 150;
    const R = 58;
    g.setStroke(new BasicStroke(2));
    g.setColor(new Color(230, 234, 240, 26));
    g.drawOval(x - R, y - R, R * 2, R * 2);
    g.setColor(new Color(230, 234, 240, 30));
    g.fillOval(x - 16, y - 16, 32, 32);
    g.setStroke(new BasicStroke(1));
  }

  private renderButton(g: Graphics2D, b: Btn, enabled: boolean, label: string): void {
    const accent = GamePalette.accent();
    const pressed = this.flash.get(b.id) ?? 0;
    const isHeld = [...this.held.values()].includes(b.id);
    const lit = Math.max(pressed, isHeld ? 1 : 0);

    const cx = Math.trunc(b.cx);
    const cy = Math.trunc(b.cy);
    const r = b.r;

    // Glow when pressed
    if (lit > 0.02) {
      const a = Math.trunc(lit * 70);
      g.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), a));
      g.fillOval(cx - r - 10, cy - r - 10, (r + 10) * 2, (r + 10) * 2);
    }

    // Body. Kept translucent on purpose: the buttons sit inside live play space (the arena is
    // the whole frame), so an opaque body would hide the player and any enemy homing in through
    // the corner the thumb is already covering. The stroke and label carry the readability.
    const bodyA = enabled ? 40 + Math.trunc(lit * 60) : 18;
    g.setColor(new Color(22, 30, 44, bodyA));
    g.fillOval(cx - r, cy - r, r * 2, r * 2);
    g.setColor(enabled
      ? new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), bodyA + 40)
      : new Color(80, 92, 108, 60));
    g.setStroke(new BasicStroke(2));
    g.drawOval(cx - r, cy - r, r * 2, r * 2);
    g.setStroke(new BasicStroke(1));

    // Label
    g.setFont(r >= 44 ? TouchControls.FONT_BTN : TouchControls.FONT_BTN_SM);
    const fm = g.getFontMetrics();
    g.setColor(enabled ? new Color(230, 234, 240, 215) : new Color(120, 132, 148, 120));
    g.drawString(label, cx - Math.trunc(fm.stringWidth(label) / 2), cy + Math.trunc(fm.getAscent() / 2) - 1);
  }
}
